import clsx from 'clsx'
import SolidHeartIcon from 'LuxKit/Icons/solid/SolidHeartIcon'
import type { SvgIconComponent } from 'LuxKit/SvgIcon'
import { SVG_ICON_SIZE_CSS_VAR, StyledSvgIcon } from 'LuxKit/SvgIcon'
import Caption from 'LuxKit/Typography/Caption'
import { setCSSVar } from 'lib/theme/themeUtils'
import { rem } from 'polished'
import type { ComponentProps } from 'react'
import { memo } from 'react'
import styled, { useTheme } from 'styled-components'
import { SAVED_ACTIVE_PIN_COLOR } from 'tripPlanner/utils/tripItemPin'

export const TEARDROP_PIN_WIDTH = 30
export const TEARDROP_PIN_HEIGHT = 36

const StyledSvg = styled.svg`
  pointer-events: none;
  color: ${(props) => props.theme.palette.brand.primary.normal};
  overflow: visible;

  & > path {
    pointer-events: all;
    cursor: pointer;
  }

  &.isActive {
    color: ${(props) => props.theme.palette.highlight.secondary.normalForeground};
  }
`

const NumberContainer = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  width: 100%;
  height: 80%;
  color: ${(props) => props.theme.palette.neutral.default.eight};
  font-size: ${rem(12)};
  font-weight: bold;
  user-select: none;
`

const CaptionText = styled(Caption)`
  color: ${(props) => props.theme.palette.messaging.favourite.normalForeground};
  text-shadow: -1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff,
    1px 1px 0 #fff;
`

const IconContainer = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: start;
  padding-top: ${rem(6)};
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  color: ${(props) => props.theme.palette.neutral.default.eight};

  > ${StyledSvgIcon} {
    ${setCSSVar(SVG_ICON_SIZE_CSS_VAR, '50%')}
  }
`

const CaptionTextContainer = styled.div`
  position: absolute;
  top: ${rem(-20)};
  left: 50%;
  transform: translateX(-50%);
  margin: auto;
  white-space: nowrap;
  text-align: center;
  z-index: 9999;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease;
`

const Container = styled.div`
  position: relative;
  width: auto;
  height: auto;

  &:hover ${CaptionTextContainer} {
    opacity: 1;
    visibility: visible;
  }
`

interface Props extends ComponentProps<'div'> {
  className?: string
  isActive?: boolean
  pinColour?: string
  number?: number
  icon?: SvgIconComponent
  text?: string
}

function TeardropPin({ isActive, className, pinColour, number, icon: Icon, text }: Props) {
  const theme = useTheme()
  const defaultColor = theme.palette.brand.primary.normal
  const isSavedpin = Icon === SolidHeartIcon || pinColour === SAVED_ACTIVE_PIN_COLOR

  return (
    <Container className={className}>
      <StyledSvg
        className={clsx({ isActive })}
        width={TEARDROP_PIN_WIDTH}
        height={TEARDROP_PIN_HEIGHT}
        viewBox="0 0 24 30"
      >
        <path
          d="M20.9763 19.4042L20.3335 20.2149H20.3816L12.1218 29.4448C12.0559 29.5184 11.9442 29.5184 11.8782 29.4448L3.61843 20.2149H3.66649L3.02373 19.4042C1.44474 17.4128 0.5 14.8851 0.5 12.1322C0.5 5.70275 5.65388 0.5 12 0.5C18.3461 0.5 23.5 5.70275 23.5 12.1322C23.5 14.8851 22.5553 17.4128 20.9763 19.4042Z"
          fill={pinColour || defaultColor}
          stroke={isSavedpin ? theme.palette.neutral.default.five : theme.palette.neutral.default.eight}
          strokeWidth={isSavedpin ? '1' : '2.5'}
        />
      </StyledSvg>
      {Icon && <IconContainer><Icon colour={isSavedpin ? 'favourite' : 'neutral-eight'} /></IconContainer>}
      {number && !Icon && <NumberContainer>{number}</NumberContainer>}
      <CaptionTextContainer>
        <CaptionText variant="large" weight="bold">
          {text}
        </CaptionText>
      </CaptionTextContainer>
    </Container>
  )
}

export default memo(TeardropPin)
